// @flow

import $ from 'jquery';
import * as React from 'react';
import { SearchHits } from './searchResults/SearchHits';
import { SearchInstructions } from './searchResults/SearchInstructions';
import { SearchResultFooter } from './searchResults/SearchResultFooter';
import { SearchSortSelect } from './searchResults/SearchSortSelect';
import { ViinitieBanner } from './searchResults/ViinitieBanner';
import { Pagination } from 'common/components/Pagination';
import { Render } from 'common/components/Render';
import { isFeatureEnabled } from 'common/features';
import { SearchActionCreators } from 'search/actions/SearchActionCreators';
import { trackClickedSearchResult } from 'search/analytics';
import { VenuePreview } from 'search/components/VenuePreview';
import type {
  VenueResult,
  ResultsMeta,
  Params as SearchParams
} from 'search/types';

type OwnProps = {|
  params: SearchParams,
  results: {
    meta: ResultsMeta,
    venues: Array<VenueResult>,
    featuredVenues?: Array<VenueResult>,
    highPriorityFeaturedVenues?: Array<VenueResult>,
    ...
  },
  highlightCapacitySitting: boolean,
  openInNewTab: boolean,
  activeResultId?: number,
  isLoading?: boolean,
  features: { edit_relevance?: boolean, ... }
|};

export type Props = {|
  ...OwnProps,
  staticPagesEnabled: boolean
|};

export class SearchResultsUI extends React.Component<Props> {
  _container: React.RefObject<HTMLDivElement | null> = React.createRef();

  render(): React.Node {
    const { highlightCapacitySitting, openInNewTab, staticPagesEnabled } =
      this.props;
    const meta = this.props.results.meta;
    const resultsFound = !!meta.total;
    const noMorePages =
      !meta.total_pages || meta.current_page === meta.total_pages;

    const isPreviewActive = (venue: VenueResult) =>
      this.props.activeResultId === venue.id;

    const { highPriorityFeaturedVenues = [] } = this.props.results;
    const superFeaturedVenuePreviewElements = highPriorityFeaturedVenues.map(
      (venue, index) => {
        return (
          <li key={venue.id} data-testid="featured-search-result">
            <VenuePreview
              variant={'FEATURED'}
              venue={venue}
              highlightCapacity={
                highlightCapacitySitting ? 'SITTING' : 'STANDING'
              }
              openInNewTab={openInNewTab}
              isActive={isPreviewActive(venue)}
              onClickVenueLink={() =>
                trackClickedSearchResult(venue, meta, index, 'SUPER FEATURED')
              }
              priority={'HIGH'}
            />
          </li>
        );
      }
    );

    const { featuredVenues = [] } = this.props.results;
    const featuredVenuePreviewElements = featuredVenues.map((venue, index) => {
      return (
        <li key={venue.id} data-testid="featured-search-result">
          <VenuePreview
            variant={'FEATURED'}
            venue={venue}
            highlightCapacity={
              highlightCapacitySitting ? 'SITTING' : 'STANDING'
            }
            openInNewTab={openInNewTab}
            isActive={isPreviewActive(venue)}
            onClickVenueLink={() =>
              trackClickedSearchResult(venue, meta, index, 'FEATURED')
            }
            priority={'REGULAR'}
          />
        </li>
      );
    });

    const venuePreviewElements = this.props.results.venues.map(
      (venue, index) => {
        return (
          <VenuePreview
            variant={'BASIC'}
            venue={venue}
            highlightCapacity={
              highlightCapacitySitting ? 'SITTING' : 'STANDING'
            }
            openInNewTab={openInNewTab}
            isActive={isPreviewActive(venue)}
            key={venue.id}
            features={this.props.features}
            onClickVenueLink={() =>
              trackClickedSearchResult(
                venue,
                meta,
                index + featuredVenues.length,
                'NORMAL'
              )
            }
          />
        );
      }
    );

    const banner = (() => {
      if (isFeatureEnabled('viinitie_search_banner') && this.props.params.et) {
        return <ViinitieBanner eventType={`${this.props.params.et}`} />;
      } else {
        return null;
      }
    })();

    return (
      <div
        className="m-search-results b-overlay-container"
        ref={this._container}
      >
        <div className="b-search-results-sorting">
          <SearchHits
            offsetStart={meta.offset_start}
            offsetEnd={meta.offset_end}
            total={meta.total}
          />
          <SearchSortSelect
            value={this.props.params.sort}
            onValue={this._onSortChange}
          />
        </div>
        {banner}
        <ul className="b-search-result-list" data-testid="search-results">
          {superFeaturedVenuePreviewElements}
          {featuredVenuePreviewElements}
          {venuePreviewElements}
        </ul>
        <Pagination
          currentPage={meta.current_page}
          totalPages={meta.total_pages}
          firstPageURL={meta.first_page_url}
          lastPageURL={null}
          nextPageURL={meta.next_page_url}
          previousPageURL={meta.previous_page_url}
          onPageChange={this._onPageChange}
        />
        <Render when={noMorePages}>
          <SearchInstructions
            resultsFound={resultsFound}
            alternativeSite={meta.alternative_site}
          />
        </Render>
        <SearchResultFooter staticPagesEnabled={staticPagesEnabled} />
        <div className="m-search-results-fade" />
        {this.props.isLoading && (
          <div className="b-overlay b-overlay--full b-overlay--light" />
        )}
      </div>
    );
  }

  _onSortChange: (sorting: ?string) => void = sorting => {
    SearchActionCreators.changeSorting(sorting);
  };

  _onPageChange: (nextPage: number) => void = nextPage => {
    SearchActionCreators.changePage(nextPage);
    this._scrollToResults();
  };

  _scrollToResults: () => void = () => {
    const offsetY =
      $(this._container.current).offset().top - $('.m-navbar').height();
    window.scrollTo(0, offsetY);
  };
}

type ContainerState = {|
  staticPagesEnabled: boolean
|};

export class SearchResultsContainer extends React.Component<
  OwnProps,
  ContainerState
> {
  state: ContainerState = {
    staticPagesEnabled: isFeatureEnabled('static_pages')
  };

  render(): React.Node {
    return <SearchResultsUI {...this.props} {...this.state} />;
  }
}
